import Plot from 'react-plotly.js';
import type { Data } from 'plotly.js';
import type { ScenarioResult } from '../../types';

const PLOT_LAYOUT = {
  paper_bgcolor: '#ffffff',
  plot_bgcolor: '#ffffff',
  font: { family: '-apple-system, BlinkMacSystemFont, sans-serif', size: 12, color: '#374151' },
  margin: { t: 40, r: 20, b: 50, l: 55 },
};

interface Props {
  scenarios: ScenarioResult[];
  currentGdp: number;
  pmTarget: number;
}

export function ScenarioComparisonChart({ scenarios, currentGdp, pmTarget }: Props) {
  const names = scenarios.map((s) => s.scenario_name.replace(' Reform', '').replace(' Case', ''));
  const gdps = scenarios.map((s) => s.simulated_gdp_growth);

  return (
    <Plot
      data={[
        {
          type: 'bar',
          x: names,
          y: gdps,
          marker: { color: '#2563EB' },
          name: 'Simulated GDP',
        },
        {
          type: 'scatter',
          mode: 'markers',
          x: ['Incremental', 'Moderate', 'Accelerated', 'Transformational', 'Custom'],
          y: [currentGdp, currentGdp, currentGdp, currentGdp, currentGdp],
          marker: { color: '#111827', size: 10, symbol: 'diamond' },
          name: 'Current verified',
        },
      ]}
      layout={{
        ...PLOT_LAYOUT,
        title: 'GDP Scenario Comparison',
        yaxis: { title: '% real GDP growth', gridcolor: '#f3f4f6' },
        shapes: [{
          type: 'line', xref: 'paper', yref: 'y',
          x0: 0, x1: 1, y0: pmTarget, y1: pmTarget,
          line: { color: '#16A34A', dash: 'dash', width: 2 },
        }],
        annotations: [{
          xref: 'paper', yref: 'y', x: 1, y: pmTarget,
          text: `PM target ${pmTarget}%`, showarrow: false,
          font: { size: 10, color: '#16A34A' }, xanchor: 'right',
        }],
        showlegend: true,
        legend: { orientation: 'h', y: -0.2 },
      }}
      config={{ displayModeBar: false, responsive: true }}
      style={{ width: '100%', height: 360 }}
    />
  );
}

interface PathProps {
  scenarios: ScenarioResult[];
  pmTarget: number;
  baseline: number;
}

export function FiveYearPathChart({ scenarios, pmTarget, baseline }: PathProps) {
  const years = ['Year 1', 'Year 2', 'Year 3', 'Year 4', 'Year 5'];
  const colors = ['#93C5FD', '#60A5FA', '#2563EB', '#1D4ED8', '#1E3A8A'];

  return (
    <Plot
      data={scenarios.map((s, i) => ({
        type: 'scatter',
        mode: 'lines+markers',
        name: s.scenario_name.replace(' Reform', '').replace(' Case', ''),
        x: years,
        y: s.five_year_path,
        line: { color: colors[i % colors.length], width: 2 },
      }))}
      layout={{
        ...PLOT_LAYOUT,
        title: 'Five-Year GDP Realization Path',
        yaxis: { title: '% GDP growth', gridcolor: '#f3f4f6' },
        shapes: [{
          type: 'line', xref: 'paper', yref: 'y',
          x0: 0, x1: 1, y0: pmTarget, y1: pmTarget,
          line: { color: '#16A34A', dash: 'dot' },
        }],
      }}
      config={{ displayModeBar: false, responsive: true }}
      style={{ width: '100%', height: 360 }}
    />
  );
}

interface WaterfallProps {
  selected: ScenarioResult;
}

export function DecompositionWaterfall({ selected }: WaterfallProps) {
  const labels = [
    'Baseline',
    ...selected.area_contributions.map((a) => a.policy_area_name),
    'Interactions',
    'Simulated GDP',
  ];
  const values = [
    selected.baseline_growth,
    ...selected.area_contributions.map((a) => a.contribution_pp),
    selected.interaction_effects_pp,
    selected.simulated_gdp_growth,
  ];
  const measures = [
    'absolute',
    ...selected.area_contributions.map(() => 'relative' as const),
    'relative',
    'total',
  ];
  const colors = [
    '#6B7280',
    ...selected.area_contributions.map((a) => a.color),
    '#9CA3AF',
    '#1E3A5F',
  ];

  return (
    <Plot
      data={[{
        type: 'waterfall',
        orientation: 'v',
        x: labels,
        y: values,
        measure: measures,
        connector: { line: { color: '#e5e7eb' } },
        increasing: { marker: { color: '#2563EB' } },
        decreasing: { marker: { color: '#DC2626' } },
        totals: { marker: { color: '#1E3A5F' } },
        text: values.map((v) => `${v.toFixed(2)}`),
        textposition: 'outside',
      } as Data]}
      layout={{
        ...PLOT_LAYOUT,
        title: 'Growth Decomposition — Selected Scenario',
        yaxis: { title: 'pp / % GDP', gridcolor: '#f3f4f6' },
        showlegend: false,
      }}
      config={{ displayModeBar: false, responsive: true }}
      style={{ width: '100%', height: 420 }}
    />
  );
}
